import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

// Detecta iOS (iPhone/iPad/iPod) independente do browser
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

// Detecta Safari nativo (exclui Chrome/Firefox no iOS)
const isIOSSafari = () =>
  isIOS() && /safari/i.test(navigator.userAgent) && !/crios|fxios|opios/i.test(navigator.userAgent);

// Verifica se já está rodando como PWA instalada
const isStandalone = () =>
  window.navigator.standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

const STORAGE_KEY = 'pwaPromptDismissedUntil';

const isDismissed = () => {
  try {
    const until = localStorage.getItem(STORAGE_KEY);
    return until && Date.now() < Number(until);
  } catch { return false; }
};

const dismissFor = (days) => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now() + days * 86_400_000)); } catch {}
};

// ─────────────────────────────────────────────────────────────────────────────

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null); // Android
  const [show, setShow]         = useState(false);
  const [platform, setPlatform] = useState(null); // 'android' | 'ios'

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    if (isIOSSafari()) {
      // Pequeno delay para não assustar logo ao abrir
      const t = setTimeout(() => { setPlatform('ios'); setShow(true); }, 2500);
      return () => clearTimeout(t);
    }

    // Android / Chrome / Edge Desktop — capturar o evento nativo
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('android');
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShow(false);
    // Instalou ou recusou no diálogo nativo — não mostrar mais por 30 dias
    dismissFor(outcome === 'accepted' ? 365 : 30);
  };

  const handleDismiss = () => {
    setShow(false);
    dismissFor(7); // Volta a aparecer em 7 dias
  };

  if (!show) return null;

  // ── iOS — balão informativo ───────────────────────────────────────────────
  if (platform === 'ios') {
    return (
      <div className="fixed bottom-20 left-3 right-3 z-50 lg:hidden animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-slate-900 text-white rounded-2xl shadow-2xl p-4 relative">
          {/* Setinha para baixo (aponta para a barra do Safari) */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-slate-900 rotate-45 rounded-sm" />

          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center shrink-0">
              <img src="/icone.png" alt="Pelada Match" className="w-8 h-8 rounded-lg" />
            </div>
            <div>
              <p className="font-bold text-sm">Adicionar à Tela de Início</p>
              <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                Toque em{' '}
                <span className="inline-flex items-center gap-0.5 bg-slate-700 px-1.5 py-0.5 rounded text-white font-medium">
                  <Share className="w-3 h-3" /> Compartilhar
                </span>
                {' '}e depois em{' '}
                <span className="bg-slate-700 px-1.5 py-0.5 rounded font-medium">
                  Adicionar à Tela de Início
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Android / Chrome — banner com botão nativo ────────────────────────────
  return (
    <div className="fixed bottom-20 left-3 right-3 z-50 lg:hidden animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <div className="w-11 h-11 shrink-0">
          <img src="/icone.png" alt="Pelada Match" className="w-full h-full rounded-xl" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-800 text-sm">Instalar Pelada Match</p>
          <p className="text-xs text-slate-400 mt-0.5">Adicione à tela inicial para acesso rápido</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleDismiss}
            className="p-1.5 text-slate-300 hover:text-slate-500"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={handleInstall}
            className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-blue-700 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Instalar
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
